import * as path from "path";

import { Express } from "express";
import mime from "mime-types";
import { FarmConfig } from "Tools/src/CandyCLI";

import { readFormConfig } from "../../../BuildTasks/ResolveFormInfo";
import { httpGet } from "../../../Commons/HttpServer/AttributeRouting/Methods";
import { NotFoundWebError } from "../../../Commons/HttpServer/AttributeRouting/NotFoundWebError";
import { httpType, httpUrlParam } from "../../../Commons/HttpServer/AttributeRouting/Params";
import { httpReturn } from "../../../Commons/HttpServer/AttributeRouting/Results";
import { StreamResult } from "../../../Commons/HttpServer/AttributeRouting/Results/StreamResultHandler";
import { SingletonController } from "../../../Commons/HttpServer/ExpressWrapper/SingletonController";
import { WellKnownDirectories } from "../../../../../Common/WellKnownDirectories";

import { BuildFormDevServer } from "./DevServers/BuildFormDevServer";
import { FarmDirectory } from "./Directories/FarmDirectory";
import { IGeneratedServerSideSourcesProvider } from "./IGeneratedServerSideSourcesProvider";

export class BuildServerBuildFormsController
    extends SingletonController
    implements IGeneratedServerSideSourcesProvider
{
    private allFarmDirectory: FarmDirectory[];
    private readonly allFarmDirectoryPath: string[];
    private nextFreePort = 12001;
    private readonly devServers: { [gfv: string]: undefined | BuildFormDevServer } = {};
    private readonly candyModulesDirectory: string;
    private readonly allFarmConfig: FarmConfig[];
    private readonly useGeneratorFromLocalSources: boolean;
    private readonly engineDirectory: string;
    private readonly nightly: boolean;
    private readonly buildDebugOnly: boolean;

    public constructor(
        allFarmConfig: FarmConfig[],
        candyModulesDirectory: string,
        useGeneratorFromLocalSources: boolean,
        engineDirectory: string,
        buildDebugOnly: boolean,
        nightly: boolean = false
    ) {
        super();
        this.nightly = nightly;
        this.allFarmDirectory = allFarmConfig.map(farm => new FarmDirectory(farm.farmDirectory));
        this.allFarmDirectoryPath = allFarmConfig.map(farm => farm.farmDirectory);
        this.candyModulesDirectory = candyModulesDirectory;
        this.allFarmConfig = allFarmConfig;
        this.useGeneratorFromLocalSources = useGeneratorFromLocalSources;
        this.engineDirectory = engineDirectory;
        this.buildDebugOnly = buildDebugOnly;
    }

    public async setupMiddleware(expressApplication: Express) {
        expressApplication.use("/force-gfvs", (_, __, next) => {
            this.resetAllFarmDirectories();
            next();
        });
    }

    private resetAllFarmDirectories() {
        this.allFarmDirectory = this.allFarmConfig.map(i => new FarmDirectory(i.farmDirectory));
    }

    @httpGet(
        "/:update.hot-update.json",
        httpReturn.StreamWithMime("application/json"),
        httpUrlParam("update", httpType.String)
    )
    public async getDevServerUpdateJson(updateName: string): Promise<StreamResult> {
        return await this.getFirstExistsFileAsStreamFromDevServer(`static/${updateName}.hot-update.json`);
    }

    @httpGet(
        "/:update.hot-update.js",
        httpReturn.StreamWithMime("application/javascript"),
        httpUrlParam("update", httpType.String)
    )
    public async getDevServerUpdateJs(updateName: string): Promise<StreamResult> {
        return await this.getFirstExistsFileAsStreamFromDevServer(`static/${updateName}.hot-update.js`);
    }

    @httpGet(
        "/:update.hot-update.js.map",
        httpReturn.StreamWithMime("application/json"),
        httpUrlParam("update", httpType.String)
    )
    public async getDevServerUpdateJsMap(updateName: string): Promise<StreamResult> {
        return await this.getFirstExistsFileAsStreamFromDevServer(`static/${updateName}.hot-update.js.map`);
    }

    @httpGet(
        /^\/public\/scripts\/(((\d{6,})(.[\w\-]*)?(.min)?).js)$/i,
        httpReturn.StreamWithMime("application/javascript"),
        httpUrlParam("0", httpType.String),
        httpUrlParam("1", httpType.String),
        httpUrlParam("2", httpType.String)
    )
    public async getFormScript(fileName: string, _fileNameWithoutExt: string, gfv: string): Promise<StreamResult> {
        const buildFormDevServer = await this.getOrCreateBuildFormDevServer(gfv);
        return new StreamResult(await buildFormDevServer.getFileAsStream(`static/public/scripts/${fileName}`));
    }

    @httpGet(
        /^\/public\/styles\/(((\d{6,})(.[\w\-]*)?(.min)?).css)$/i,
        httpReturn.StreamWithMime("text/css"),
        httpUrlParam("0", httpType.String),
        httpUrlParam("1", httpType.String),
        httpUrlParam("2", httpType.String),
        httpUrlParam("3", httpType.String)
    )
    public async getFormStyle(
        fileName: string,
        _fileNameWithoutExt: string,
        gfv: string,
        theme: undefined | string
    ): Promise<StreamResult> {
        const buildFormDevServer = await this.getOrCreateBuildFormDevServer(gfv);
        return new StreamResult(
            await buildFormDevServer.getFileAsStream(`static/public/styles/${fileName.replace(theme ?? "", "")}`)
        );
    }

    @httpGet("/public/images/:filename", httpReturn.Stream, httpUrlParam("filename", httpType.String))
    public async getFormImages(fileName: string): Promise<StreamResult> {
        return await this.getFirstExistsFileAsStreamFromDevServer(
            `static/public/images/${fileName}`,
            mime.lookup(fileName)
        );
    }

    @httpGet("/normalize/:gfv.All.kclang", httpReturn.Stream, httpUrlParam("gfv", httpType.String))
    public async getAllKclangFile(gfv: string): Promise<StreamResult> {
        const buildFormDevServer = await this.getOrCreateBuildFormDevServer(gfv);
        const stream = await buildFormDevServer.getGeneratedFileAsStream(`All.kclang`);
        return new StreamResult(stream);
    }

    @httpGet("/schemas/:gfv.extendedSchema.json", httpReturn.Stream, httpUrlParam("gfv", httpType.String))
    public async getExtendedSchemaFile(gfv: string): Promise<StreamResult> {
        const buildFormDevServer = await this.getOrCreateBuildFormDevServer(gfv);
        const stream = await buildFormDevServer.getGeneratedFileAsStream(`extendedSchema.json`);
        return new StreamResult(stream);
    }

    private async getFirstExistsFileAsStreamFromDevServer(filename: string, mimeType?: string | false) {
        for (const devServer of Object.values(this.devServers)) {
            if (devServer != undefined) {
                try {
                    return new StreamResult(await devServer.getFileAsStream(filename), mimeType);
                } catch {
                    // try next server
                }
            }
        }
        throw new NotFoundWebError("File not found", true);
    }

    public async getAllAttachmentPathFileNames(): Promise<string[]> {
        return (await Promise.all(this.allFarmDirectory.map(i => i.getAllGfvs()))).flat().map(gfv => `${gfv}.json`);
    }

    public async getAttachmentPathsFileContent(gfv: string): Promise<NodeJS.ReadableStream> {
        const buildFormDevServer = await this.getOrCreateBuildFormDevServer(gfv);
        const resultStream = await buildFormDevServer.getGeneratedFileAsStream(`AttachmentPaths.json`);
        return resultStream;
    }

    public async getAttachmentPathsWithGfvsFileContent(gfv: string): Promise<NodeJS.ReadableStream> {
        const buildFormDevServer = await this.getOrCreateBuildFormDevServer(gfv);
        const resultStream = await buildFormDevServer.getGeneratedFileAsStream(`AttachmentPathsWithGfvs.json`);
        return resultStream;
    }

    public async getAllNormalizationFileNames(): Promise<string[]> {
        const result: string[] = [];
        const gfvInfos = (await Promise.all(this.allFarmDirectory.map(i => i.getAllGfvWithLocations()))).flat();
        for (const { gfv, formPath } of gfvInfos) {
            const formConfig = await readFormConfig(path.join(formPath, WellKnownDirectories.FormJsonFileName));
            const useServerSideFLangNormalization = formConfig.settings?.useServerSideFLangNormalization ?? false;
            if (useServerSideFLangNormalization) {
                result.push(`${gfv}.normalize`);
            }
        }
        return result;
    }

    public async getNormalizationFileContent(gfv: string): Promise<NodeJS.ReadableStream | null> {
        const gfvInfos = (await Promise.all(this.allFarmDirectory.map(i => i.getAllGfvWithLocations()))).flat();
        const gfvInfo = gfvInfos.find(x => x.gfv === gfv);
        if (gfvInfo == null) {
            return null;
        }
        const formConfig = await readFormConfig(path.join(gfvInfo.formPath, WellKnownDirectories.FormJsonFileName));
        const useServerSideFLangNormalization = formConfig.settings?.useServerSideFLangNormalization ?? false;
        if (!useServerSideFLangNormalization) {
            return null;
        }
        const buildFormDevServer = await this.getOrCreateBuildFormDevServer(gfv);
        const resultStream = await buildFormDevServer.getGeneratedFileAsStream(`ServerSide.normalize`);
        return resultStream;
    }

    private async getOrCreateBuildFormDevServer(gfv: string): Promise<BuildFormDevServer> {
        let devServer = this.devServers[gfv];
        if (devServer == undefined) {
            devServer = new BuildFormDevServer(
                this.nextFreePort++,
                this.allFarmDirectoryPath,
                gfv,
                this.candyModulesDirectory,
                this.useGeneratorFromLocalSources,
                this.engineDirectory,
                this.buildDebugOnly,
                this.nightly
            );
            this.devServers[gfv] = devServer;
            await devServer.start();
        }
        return devServer;
    }
}
